import { formatReferenceCopy } from 'utils/typography'
import './proposal-service-lines-editor.scss'

import {
  useEffect,
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Box,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material'
import { COLORS } from 'config/constants'
import { usePreviewProposalDraftMutation } from 'api/applications-manager'

import ProposalServiceLineAmounts from './proposal-service-line-amounts'
import ProposalServicesTotalSummary from './proposal-services-total-summary'
import {
  parseProposalMoneyInput,
  proposalServiceLineTitleKey,
} from '../proposal-service-line-utils'

const PREVIEW_DEBOUNCE_MS = 300

const ProposalServiceLinesEditor = ({
  applicationPublicId,
  serviceLines,
  currencyCode,
  onServiceLinesChange,
}: ApplicationProposalServiceLinesEditorProps) => {
  const { t } = useTranslation()
  const [ previewProposalDraft ] = usePreviewProposalDraftMutation()
  const [ preview, setPreview ] = useState<ApplicationProposalServicesComparison | null>(null)

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(() => {
      previewProposalDraft({
        publicId: applicationPublicId,
        serviceLines: serviceLines.map((line) => ({ kind: line.kind, amount: line.amount })),
      })
        .unwrap()
        .then((result) => {
          if (!cancelled) setPreview(result)
        })
        .catch(() => undefined)
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [ applicationPublicId, serviceLines, previewProposalDraft ])

  const updateLine = (kind: string, patch: Partial<ApplicationProposalServiceLineForm>) => {
    onServiceLinesChange(
      serviceLines.map((line) => (line.kind === kind ? { ...line, ...patch } : line)),
    )
  }

  const acceptLineAsIs = (kind: string) => {
    onServiceLinesChange(
      serviceLines.map((line) => (
        line.kind === kind
          ? { ...line, amount: parseProposalMoneyInput(line.original_amount) }
          : line
      )),
    )
  }

  return (
    <Box className='application-proposal-service-lines-editor'>
      <ProposalServicesTotalSummary
        comparison={preview?.total}
        currencyCode={currencyCode}
        variant='in-card-body'
      />
      {serviceLines.map((line) => (
        <Box key={line.kind} className='application-proposal-service-lines-editor__block'>
          <Box className='application-proposal-service-lines-editor__block-head'>
            <Typography variant='subtitle2' component='h3'>
              {formatReferenceCopy(t(proposalServiceLineTitleKey(line.kind)))}
            </Typography>
            <ProposalServiceLineAmounts
              comparison={preview?.lines.find((row) => row.kind === line.kind)}
              currencyCode={currencyCode}
              layout='stacked'
            />
          </Box>
          <Box className='application-proposal-service-lines-editor__block-body'>
            <Box className='application-proposal-service-lines-editor__amount-row'>
              <Typography
                component='label'
                variant='body2'
                className='application-proposal-service-lines-editor__amount-label'
                color='text.primary'
                htmlFor={`proposal-line-amount-${line.kind}`}
              >
                {formatReferenceCopy(t('applications__text__proposalLineAmount'))}
              </Typography>
              <TextField
                id={`proposal-line-amount-${line.kind}`}
                size='small'
                className='application-proposal-service-lines-editor__amount-input'
                inputProps={{ inputMode: 'decimal' }}
                value={line.amount}
                onChange={(e) => updateLine(line.kind, { amount: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Typography variant='body2'>
                        {currencyCode}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
              <Link
                component='button'
                type='button'
                variant='body2'
                underline='hover'
                className='application-proposal-service-lines-editor__accept'
                sx={{
                  color: COLORS.BLUE,
                  fontWeight: 400,
                  '&:hover': { color: COLORS.BLUE_DARK },
                }}
                onClick={() => acceptLineAsIs(line.kind)}
              >
                <Typography component='span' variant='inherit'>
                  {formatReferenceCopy(t('applications__text__proposalLineAcceptAsIs'))}
                </Typography>
              </Link>
            </Box>

            <TextField
              fullWidth
              size='small'
              multiline
              minRows={3}
              maxRows={4}
              className='application-proposal-service-lines-editor__comment'
              label={<Typography component='span' variant='inherit'>
                {formatReferenceCopy(t('applications__text__proposalLineComment'))}
              </Typography>}
              placeholder={formatReferenceCopy(t('applications__text__proposalLineCommentPlaceholder'))}
              value={line.comment}
              onChange={(e) => updateLine(line.kind, { comment: e.target.value })}
            />
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default ProposalServiceLinesEditor
